import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import i18next, { tEnum, supportedLanguages } from '../../../lang';
import { User } from './UserManagement';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { adminStyles } from '../../../theme/adminStyles';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import InventoryIcon from '@mui/icons-material/Inventory';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import {rootStore} from "../../../stores/RootStore";
import {getLevelColor, getStatusColor} from './UserManagement';
import { isPasswordValid, PASSWORD_RULE_MESSAGE } from '../../../utils/passwordValidator';

interface UserProductInfo {
  id: number;
  title: string;
  curriculumName: string;
  schoolType: string;
  targetGrade: string;
  volume: string;
  subTitle: string;
  price: number;
  status: string;
}

interface UserProductSetInfo {
  id: number;
  name: string;
  price: number;
}

interface NewAddress {
  id: number;
  userId: string;
  postalCode: string;
  prefecture: string;
  city: string;
  streetAddress: string;
  building: string;
  aliasName: string;
  isDefault: number;
  createdAt: string;
  updatedAt: string;
}

const UserDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authStore, viewStatusStore } = rootStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFoundDialogOpen, setNotFoundDialogOpen] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const [paymentMonthDialogOpen, setPaymentMonthDialogOpen] = useState(false);
  const [paymentDateDialogOpen, setPaymentDateDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDueDate, setSelectedDueDate] = useState(0);
  const [selectedPaymentMonth, setSelectedPaymentMonth] = useState(0);
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(0);
  const [preferredLanguageDialogOpen, setPreferredLanguageDialogOpen] = useState(false);
  const [selectedPreferredLanguage, setSelectedPreferredLanguage] = useState('');
  const [editingContact, setEditingContact] = useState<any>(null);
  const [editingContactValue, setEditingContactValue] = useState('');
  const [openNewContactDialog, setOpenNewContactDialog] = useState(false);
  const [newContactType, setNewContactType] = useState('EMAIL');
  const [newContactValue, setNewContactValue] = useState('');
  const [openNewAddressDialog, setOpenNewAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState<NewAddress>({
    id: 0,
    userId: '',
    postalCode: '',
    prefecture: '',
    city: '',
    streetAddress: '',
    building: '',
    aliasName: '',
    isDefault: 0,
    createdAt: '',
    updatedAt: '',
  });
  const [postalCodeError, setPostalCodeError] = useState<string>('');
  const [editingAddress, setEditingAddress] = useState<NewAddress | null>(null);
  const [products, setProducts] = useState<UserProductInfo[]>([]);
  const [productSets, setProductSets] = useState<UserProductSetInfo[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = id ? id : authStore.user.id;
        const response = await axios.get(`/api/v1/admin/users/${userId}`);
        setUser(response.data);
        setError(null);
        await fetchProductMappings(userId);
      } catch (error: any) {
        console.error('Failed to fetch user detail:', error);
        setNotFoundDialogOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const fetchUserDetail = async () => {
    try {
      const userId = id ? id : authStore.user.id;
      const response = await axios.get(`/api/v1/admin/users/${userId}`);
      setUser(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch user detail:', error);
    }
  };


  const handlePasswordChange = async () => {
    setOpenPasswordDialog(false);
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      await axios.put(`/api/v1/admin/users/password/${user.id}`, {
        newPassword: newPassword
      });
      setNewPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleStatusChange = async () => {
    try {
      await handleSaveEdit('status');
      setStatusDialogOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDueDateChange = async () => {
    try {
      await handleSaveEdit('dueDate');
      setDueDateDialogOpen(false);
    } catch (error) {
      console.error('Failed to update due date:', error);
    }
  };

  const handlePaymentMonthChange = async () => {
    try {
      await handleSaveEdit('paymentMonth');
      setPaymentMonthDialogOpen(false);
    } catch (error) {
      console.error('Failed to update payment month:', error);
    }
  };

  const handlePaymentDateChange = async () => {
    try {
      await handleSaveEdit('paymentDate');
      setPaymentDateDialogOpen(false);
    } catch (error) {
      console.error('Failed to update payment date:', error);
    }
  };

  const handlePreferredLanguageChange = async () => {
    try {
      await handleSaveEdit('preferredLanguage');
      setPreferredLanguageDialogOpen(false);
    } catch (error) {
      console.error('Failed to update preferred language:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleStartEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const handleSaveEdit = async (field: any) => {
    try {
      if (field === 'email' && !validateEmail(editValue)) {
        alert(i18next.t('error.invalid_email_format'));
        return;
      }
      viewStatusStore.setLoadingStatus({ open: true });
      const updatedUser = { ...user };

      if (field === 'status') {
        updatedUser[field] = selectedStatus;
      } else if (field === 'dueDate') {
        updatedUser[field] = selectedDueDate;
      } else if (field === 'paymentMonth') {
        updatedUser[field] = selectedPaymentMonth;
      } else if (field === 'paymentDate') {
        updatedUser[field] = selectedPaymentDate;
      } else if (field === 'preferredLanguage') {
        updatedUser[field] = selectedPreferredLanguage;
      } else {
        updatedUser[field] = editValue;
      }

      await axios.put(`/api/v1/admin/users/${user.id}`, updatedUser);
      await fetchUserDetail();
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleStartEditContact = (contact: any) => {
    setEditingContact(contact);
    setEditingContactValue(contact.contactValue);
  };

  const handleSaveContact = async (contact: any) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const updatedContacts = user?.additionalContacts.map(existingContact => {
        if (existingContact.id === contact.id) {
          return { ...existingContact, contactValue: editingContactValue };
        }
        return existingContact;
      });

      await axios.put(`/api/v1/admin/users/${user.id}/contacts`, updatedContacts);
      await fetchUserDetail();
      setEditingContact(null);
      setEditingContactValue('');
    } catch (error) {
      console.error('Failed to update contact:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleCancelEditContact = () => {
    setEditingContact(null);
    setEditingContactValue('');
  };

  const handleCloseNewContactDialog = () => {
    setOpenNewContactDialog(false);
    setNewContactType('EMAIL');
    setNewContactValue('');
  };

  const handleDeleteContact = async (contactId: number) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const updatedContacts = user?.additionalContacts.filter(contact => contact.id !== contactId);

      await axios.put(`/api/v1/admin/users/${user.id}/contacts`, updatedContacts);
      await fetchUserDetail();
    } catch (error) {
      console.error('Failed to delete contact:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleAddNewContact = async () => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const newContact = {
        contactType: newContactType,
        contactValue: newContactValue
      };

      const updatedContacts = [...(user?.additionalContacts || []), newContact];
      await axios.put(`/api/v1/admin/users/${user.id}/contacts`, updatedContacts);
      await fetchUserDetail();
      setOpenNewContactDialog(false);
      setNewContactType('EMAIL');
      setNewContactValue('');
    } catch (error) {
      console.error('Failed to add new contact:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const getHierarchy = (user: User) => {
    // 상위 조직 이름과 ID를 쌍으로 구성
    const segments: { name: string; userId?: string }[] = [];

    if (user.companyName) {
      segments.push({ name: user.companyName, userId: user.companyUserId });
      if (user.brandName) {
        segments.push({ name: user.brandName, userId: user.brandUserId });
        if (user.classRoomName) {
          segments.push({ name: user.classRoomName });
        } else {
          segments.push({ name: user.organizationName });
        }
      } else {
        segments.push({ name: user.organizationName });
      }
    } else {
      segments.push({ name: user.organizationName });
    }

    if (segments.length === 1) {
      return (
        <Typography component="span" fontWeight="bold">
          {segments[0].name}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {segments.map((seg, idx) => {
          const isLast = idx === segments.length - 1;
          const isLink = !isLast && seg.userId && authStore.user?.level === 'ADMIN';
          return (
            <React.Fragment key={idx}>
              {idx > 0 && <span>{' > '}</span>}
              {isLast ? (
                <Typography component="span" fontWeight="bold">{seg.name}</Typography>
              ) : isLink ? (
                <RouterLink
                  to={`/admin/users/${seg.userId}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
                >
                  {seg.name}
                </RouterLink>
              ) : (
                <span>{seg.name}</span>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  const renderEditableField = (field: string, value: any) => {
    const isEditing = editingField === field;
    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={editValue}
            type={field === 'postage' ? 'number' : undefined}
            onChange={(e) => setEditValue(e.target.value)}
            sx={{ '& .MuiInputBase-root': { height: '32px' } }}
          />
          <IconButton size="small" onClick={handleCancelEdit} sx={{ p: '4px' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleSaveEdit(field)} sx={{ p: '4px' }}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }
    const displayValue = field === 'postage' && value != null
      ? `${Number(value).toLocaleString()} ${i18next.t('common.yen')}`
      : value;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">{displayValue}</Typography>
        <IconButton size="small" onClick={() => handleStartEdit(field, value)} sx={{ p: '4px' }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const getStatusOptions = () => {
    const statusOptions = [{ value: 'APPROVED', label: i18next.t('common.approve') },
      { value: 'PENDING', label: i18next.t('common.pending_approval') },
      { value: 'DELETED', label: i18next.t('common.delete') }];

    if (user.status === 'DELETED') {
      statusOptions.push({ value: 'PENDING', label: i18next.t('common.cancel_delete') });
    }

    return statusOptions;
  };

  const sortedContacts = (contacts: any[]) => {
    const order = ['EMAIL', 'PHONE', 'FAX'];
    return [...contacts].sort((a, b) => {
      return order.indexOf(a.contactType) - order.indexOf(b.contactType);
    });
  };

  const isValidPostalCode = (code: string): boolean => {
    return /^\d{3}-?\d{4}$/.test(code);
  };

  const handlePostalCodeSearch = async () => {
    if (!newAddress.postalCode) return;

    if (!isValidPostalCode(newAddress.postalCode)) {
      setPostalCodeError(i18next.t('error.invalid_postal_code'));
      return;
    }

    // 우편번호 포맷팅 (7자리 숫자인 경우 하이픈 추가)
    let formattedPostalCode = newAddress.postalCode;
    if (/^\d{7}$/.test(newAddress.postalCode)) {
      formattedPostalCode = `${newAddress.postalCode.slice(0, 3)}-${newAddress.postalCode.slice(3)}`;
      setNewAddress({
        ...newAddress,
        postalCode: formattedPostalCode
      });
    }

    try {
      setPostalCodeError('');
      const cleanedZipcode = formattedPostalCode.replace(/-/g, '');
      const response = await axios.get(`/api/v1/auth/search-address?zipcode=${cleanedZipcode}`);

      if (response.data.status === 400) {
        setPostalCodeError(i18next.t('error.invalid_postal_code'));
        return;
      }

      if (response.data.results === null) {
        setPostalCodeError(i18next.t('error.address_not_found'));
        return;
      }

      const addressData = response.data.results[0];

      setNewAddress({
        ...newAddress,
        prefecture: addressData.address1,
        city: addressData.address2,
        streetAddress: addressData.address3,
        building: addressData.address4 ?? ""
      });
    } catch (err) {
      setPostalCodeError(i18next.t('error.postal_code_search_error'));
    }
  };

  const handlePostalCodeChange = (value: string) => {
    setPostalCodeError('');
    // 숫자와 하이픈만 허용
    const cleanedValue = value.replace(/[^\d-]/g, '');

    // 최대 8자리(하이픈 포함) 제한
    if (cleanedValue.length <= 8) {
      setNewAddress({
        ...newAddress,
        postalCode: cleanedValue
      });
    }
  };

  const handleAddressChange = (field: keyof NewAddress, value: string) => {
    setNewAddress({
      ...newAddress,
      [field]: value
    });
  };

  const handleAddNewAddress = async () => {
    try {
      if (!newAddress.postalCode || !newAddress.prefecture || !newAddress.city || !newAddress.streetAddress) {
        setError(i18next.t('error.default_address_required'));
        return;
      }

      viewStatusStore.setLoadingStatus({ open: true });

      const updatedAddresses = newAddress.isDefault === 0 ? [...user?.addresses] : user?.addresses.map(address => ({
        ...address,
        isDefault: 0
      }))

      updatedAddresses.push(newAddress)

      await axios.put(`/api/v1/admin/users/${user.id}/addresses`, updatedAddresses);

      await fetchUserDetail();
      setOpenNewAddressDialog(false);
      setNewAddress({
        id: 0,
        userId: '',
        postalCode: '',
        prefecture: '',
        city: '',
        streetAddress: '',
        building: '',
        aliasName: '',
        isDefault: 0,
        createdAt: '',
        updatedAt: '',
      });
    } catch (error) {
      console.error('Failed to add new address:', error);
      setError(i18next.t('address.add_address_failed'));
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleCancelNewAddress = () => {
    setOpenNewAddressDialog(false);
    setNewAddress({
      id: 0,
      userId: '',
      postalCode: '',
      prefecture: '',
      city: '',
      streetAddress: '',
      building: '',
      aliasName: '',
      isDefault: 0,
      createdAt: '',
      updatedAt: '',
    });
    setEditingAddress(null);
  }

  const handleEditAddress = (address: NewAddress) => {
    setEditingAddress(address);
    setNewAddress(address);
    setOpenNewAddressDialog(true);
  };

  const handleDeleteAddress = async (addressId: number) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const updatedAddresses = user?.addresses.filter(address => address.id !== addressId);
      await axios.put(`/api/v1/admin/users/${user.id}/addresses`, updatedAddresses);
      await fetchUserDetail();
    } catch (error) {
      console.error('Failed to delete address:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const fetchProductMappings = async (userId: string) => {
    try {
      const response = await axios.get(`/api/v1/admin/product-mappings/user/${userId}`);
      const mappings = response.data;

      const selectedProductIds: number[] = [];
      const selectedProductSetIds: number[] = [];

      for (const mapping of mappings) {
        if (mapping.productType === 'normal' && mapping.productId !== null) {
          selectedProductIds.push(mapping.productId);
        } else if (mapping.productType === 'set' && mapping.productSetId !== null) {
          selectedProductSetIds.push(mapping.productSetId);
        }
      }

      // 선택된 상품들만 가져오기
      if (selectedProductIds.length > 0) {
        const productsResponse = await axios.get('/api/v1/admin/products');
        const allProducts = productsResponse.data.filter((product: UserProductInfo) => product.status === 'ACTIVE');
        const selectedProducts = allProducts.filter((product: UserProductInfo) => selectedProductIds.includes(product.id));
        setProducts(selectedProducts);
      } else {
        setProducts([]);
      }

      // 선택된 상품 세트들만 가져오기
      if (selectedProductSetIds.length > 0) {
        const productSetsResponse = await axios.get('/api/v1/admin/product-sets');
        const allProductSets = productSetsResponse.data;
        const selectedProductSets = allProductSets.filter((productSet: UserProductSetInfo) => selectedProductSetIds.includes(productSet.id));
        setProductSets(selectedProductSets);
      } else {
        setProductSets([]);
      }
    } catch (error) {
      console.error('Failed to fetch product mappings:', error);
    }
  };

  const convertSchoolType = (type: string): string => {
    const schoolTypeMap: { [key: string]: string } = {
      'KINDERGARTEN': i18next.t('product.kindergarten'),
      'ELEMENTARY': i18next.t('product.elementary_school'),
      'MIDDLE': i18next.t('product.middle_school'),
      'HIGH': i18next.t('product.high_school')
    };
    return schoolTypeMap[type] || type;
  };

  // セクションカードスタイル
  const sectionCardSx = {
    borderRadius: 3,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
  };

  // セクションヘッダースタイル（CardHeader用）
  const sectionHeaderSx = {
    bgcolor: '#f8f9fa',
    borderBottom: '1px solid',
    borderColor: 'divider',
    py: 1.25,
    px: 2.5,
    '& .MuiCardHeader-title': { fontSize: '0.95rem', fontWeight: 600 },
    '& .MuiCardHeader-avatar': { minWidth: 'auto', mr: 1.5 },
    '& .MuiCardHeader-action': { mt: 0, mr: 0, alignSelf: 'center' },
  };

  const sectionIconSx = { color: 'primary.main', fontSize: '1.2rem' };

  // 情報行レンダリングヘルパー
  const renderInfoRow = (label: string, content: React.ReactNode) => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 2.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      height: '45px',
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
    }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500, flexShrink: 0, mr: 2 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {content}
      </Box>
    </Box>
  );

  if (loading && !user) return null;

  if (notFoundDialogOpen || (!loading && !user)) {
    return (
      <Dialog open={true}>
        <DialogTitle>{i18next.t('error.user_not_found')}</DialogTitle>
        <DialogContent>
          <Typography>{i18next.t('error.user_info_fetch_failed')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => navigate('/admin/users')}>
            {i18next.t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Container maxWidth="lg" sx={adminStyles.pageContainer}>
      {/* ヘッダー */}
      <Box sx={adminStyles.headerBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {id && (
            <IconButton
              onClick={() => navigate('/admin/users')}
              sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {/* 기본 정보 */}
        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<PersonIcon sx={sectionIconSx} />}
              title={i18next.t('common.basic_info')}
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {renderInfoRow('ID', <Typography variant="body2">{user.id}</Typography>)}
              {renderInfoRow(i18next.t('common.institution_name'), <Typography variant="body2">{user.organizationName}</Typography>)}
              {renderInfoRow('Email', renderEditableField('email', user.email))}
              {renderInfoRow(i18next.t('contact.phone'), renderEditableField('phone', user.phone))}
              {renderInfoRow('FAX', renderEditableField('fax', user.fax))}
              {renderInfoRow(i18next.t('auth.password'),
                <IconButton size="small" onClick={() => setOpenPasswordDialog(true)} sx={{ p: '4px' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {renderInfoRow(i18next.t('user.preferred_language'),
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {supportedLanguages.find(l => l.code === (user.preferredLanguage || 'ja'))?.nativeName}
                  </Typography>
                  <IconButton size="small" onClick={() => { setSelectedPreferredLanguage(user.preferredLanguage || 'ja'); setPreferredLanguageDialogOpen(true); }} sx={{ p: '4px' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 계정 정보 */}
        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<BadgeIcon sx={sectionIconSx} />}
              title={i18next.t('auth.account_info')}
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {renderInfoRow(i18next.t('common.role'),
                <Chip label={tEnum(user.level)} color={getLevelColor(user.level)} size="small" />
              )}
              {renderInfoRow(i18next.t('common.organization'),
                editingField === 'organizationName' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField size="small" value={editValue} onChange={(e) => setEditValue(e.target.value)}
                      fullWidth sx={{ '& .MuiInputBase-root': { height: '32px' } }} />
                    <IconButton size="small" onClick={handleCancelEdit} sx={{ p: '4px' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleSaveEdit('organizationName')} sx={{ p: '4px' }}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ fontSize: '0.875rem' }}>{getHierarchy(user)}</Box>
                    <IconButton size="small" onClick={() => handleStartEdit('organizationName', user.organizationName)} sx={{ p: '4px', flexShrink: 0 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )
              )}
              {renderInfoRow(i18next.t('common.status'),
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={tEnum(user.status)} color={getStatusColor(user.status)} size="small" />
                  {(id && authStore.user.level === 'ADMIN') && (
                    <IconButton size="small" onClick={() => { setSelectedStatus(user.status); setStatusDialogOpen(true); }} sx={{ p: '4px' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
              {user.level !== 'ADMIN' && renderInfoRow(i18next.t('user.shipping_fee'),
                authStore.user.level === 'ADMIN'
                  ? renderEditableField('postage', user.postage)
                  : <Typography variant="body2">{user.postage != null ? `${Number(user.postage).toLocaleString()} ${i18next.t('common.yen')}` : '-'}</Typography>
              )}
              {/* 許可IPアドレス: 非表示（機能未使用） */}
              {authStore.user.level === 'ADMIN' && user.level === 'ADMIN' && renderInfoRow(
                i18next.t('user.is_primary_admin'),
                <Switch
                  checked={!!user.isPrimaryAdmin}
                  onChange={async (e) => {
                    if (e.target.checked) {
                      try {
                        viewStatusStore.setLoadingStatus({ open: true });
                        const updatedUser = { ...user, isPrimaryAdmin: true };
                        await axios.put(`/api/v1/admin/users/${user.id}`, updatedUser);
                        await fetchUserDetail();
                      } catch (err) {
                        console.error('Failed to set primary admin:', err);
                      } finally {
                        viewStatusStore.setLoadingStatus({ open: false });
                      }
                    }
                  }}
                  size="small"
                />
              )}
              {renderInfoRow(i18next.t('common.created_at'),
                <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 결제 정보（CORP_PARTNERのみ） */}
        {user.level === 'CORP_PARTNER' && (
          <Grid item xs={12}>
            <Card sx={sectionCardSx}>
              <CardHeader
                avatar={<PaymentIcon sx={sectionIconSx} />}
                title={i18next.t('user.payment_info')}
                sx={sectionHeaderSx}
              />
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2.5,
                  height: '45px',
                }}>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                      {i18next.t('user.due_date')}
                    </Typography>
                    <Typography variant="body2">
                      {user.dueDate === 0 ? i18next.t('user.end_of_month') : `${user.dueDate}${i18next.t('common.day')}`}
                    </Typography>
                    {authStore.user.level === 'ADMIN' && (
                      <IconButton size="small" onClick={() => { setSelectedDueDate(user.dueDate); setDueDateDialogOpen(true); }} sx={{ p: '4px' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                      {i18next.t('user.payment_month')}
                    </Typography>
                    <Typography variant="body2">
                      {user.paymentMonth === 1 ? i18next.t('user.next_month') : i18next.t('user.month_after_next')}
                    </Typography>
                    {authStore.user.level === 'ADMIN' && (
                      <IconButton size="small" onClick={() => { setSelectedPaymentMonth(user.paymentMonth); setPaymentMonthDialogOpen(true); }} sx={{ p: '4px' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                      {i18next.t('user.payment_date')}
                    </Typography>
                    <Typography variant="body2">
                      {user.paymentDate === 0 ? i18next.t('user.end_of_month') : `${user.paymentDate}${i18next.t('common.day')}`}
                    </Typography>
                    {authStore.user.level === 'ADMIN' && (
                      <IconButton size="small" onClick={() => { setSelectedPaymentDate(user.paymentDate); setPaymentDateDialogOpen(true); }} sx={{ p: '4px' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 주소 리스트 */}
        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<LocationOnIcon sx={sectionIconSx} />}
              title={i18next.t('user.address_list')}
              action={
                <Button variant="outlined" size="small" startIcon={<AddIcon />}
                  onClick={() => setOpenNewAddressDialog(true)} sx={{ textTransform: 'none' }}>
                  {i18next.t('user.add_address')}
                </Button>
              }
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <List disablePadding>
                {user?.addresses?.map((address, index) => (
                  <React.Fragment key={address.id}>
                    <ListItem sx={{ px: 2.5, py: 1.5, bgcolor: address.isDefault === 1 ? 'action.selected' : 'inherit' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {address.isDefault === 1 && (
                            <>
                              <HomeIcon color="primary" fontSize="small" />
                              <Typography variant="body2" color="textSecondary">
                                {i18next.t('user.default_address')}
                              </Typography>
                            </>
                          )}
                          {address.aliasName && (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                              {address.aliasName}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', pr: 8, mt: 0.5 }}>
                          <Typography variant="body2">
                            {`${address.postalCode} ${address.prefecture} ${address.city} ${address.streetAddress} ${address.building || ''}`}
                          </Typography>
                        </Box>
                      </Box>
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => handleEditAddress(address)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton disabled={address.isDefault === 1} edge="end" size="small" onClick={() => handleDeleteAddress(address.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < user.addresses.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 연락처 리스트 */}
        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<ContactPhoneIcon sx={sectionIconSx} />}
              title={i18next.t('user.contact_list')}
              action={
                <Button variant="outlined" size="small" startIcon={<AddIcon />}
                  onClick={() => setOpenNewContactDialog(true)} sx={{ textTransform: 'none' }}>
                  {i18next.t('contact.add_contact')}
                </Button>
              }
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <List disablePadding>
                {sortedContacts(user.additionalContacts).map((contact, index) => (
                  <React.Fragment key={contact.id}>
                    <ListItem sx={{ px: 2.5, py: 1.5 }}>
                      {editingContact?.id === contact.id ? (
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            {tEnum(contact.contactType)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', pr: 8, mt: 0.5 }}>
                            <TextField size="small" value={editingContactValue}
                              onChange={(e) => setEditingContactValue(e.target.value)} fullWidth autoFocus />
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            {tEnum(contact.contactType)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', pr: 8, mt: 0.5 }}>
                            <Typography variant="body2">{contact.contactValue}</Typography>
                          </Box>
                        </Box>
                      )}
                      <ListItemSecondaryAction>
                        {editingContact?.id === contact.id ? (
                          <>
                            <IconButton edge="end" size="small" onClick={handleCancelEditContact} sx={{ mr: 1 }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                            <IconButton edge="end" size="small" onClick={() => handleSaveContact(contact)}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton edge="end" size="small" onClick={() => handleStartEditContact(contact)} sx={{ mr: 1 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton edge="end" size="small" onClick={() => handleDeleteContact(contact.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < user.additionalContacts.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 구매 가능한 상품 */}
        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<InventoryIcon sx={sectionIconSx} />}
              title={i18next.t('product_mapping.available_products')}
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 2 }}>
              {products.length > 0 ? (
                <Grid container spacing={1.5}>
                  {products.map((product) => (
                    <Grid item xs={12} md={6} lg={4} key={product.id}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {`${product.curriculumName || ''} ${convertSchoolType(product.schoolType) || ''} ${product.targetGrade || ''} ${product.volume || ''} ${product.title || ''} ${product.subTitle || ''}`}
                        </Typography>
                        <Chip
                          label={`${product.price.toLocaleString()} ${i18next.t('common.yen')}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    {i18next.t('product_mapping.no_available_products')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 구매 가능한 세트 상품 */}
        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <CardHeader
              avatar={<InventoryIcon sx={sectionIconSx} />}
              title={i18next.t('product_mapping.available_set_products')}
              sx={sectionHeaderSx}
            />
            <CardContent sx={{ p: 2 }}>
              {productSets.length > 0 ? (
                <Grid container spacing={1.5}>
                  {productSets.map((productSet) => (
                    <Grid item xs={12} md={6} lg={4} key={productSet.id}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>{productSet.name}</Typography>
                        <Chip
                          label={`${productSet.price.toLocaleString()} ${i18next.t('common.yen')}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    {i18next.t('product_mapping.no_available_set_products')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} fullScreen={isMobile}>
        <DialogTitle>{i18next.t('user.change_password')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={i18next.t('user.new_password')}
            type="password"
            placeholder="半角英数字8〜20文字"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={newPassword !== '' && !isPasswordValid(newPassword)}
            helperText={newPassword !== '' && !isPasswordValid(newPassword) ? PASSWORD_RULE_MESSAGE : '半角英数字8〜20文字'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary" disabled={!isPasswordValid(newPassword)}>
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('user.change_status')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
              autoFocus
              margin="dense"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ mt: 1 }}
            >
              {getStatusOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handleStatusChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
          open={dueDateDialogOpen}
          onClose={() => setDueDateDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('user.change_due_date')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
                autoFocus
                margin="dense"
                value={selectedDueDate}
                onChange={(e) => setSelectedDueDate(Number(e.target.value))}
                sx={{ mt: 1 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflowY: 'auto'
                    }
                  }
                }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day} sx={{ fontSize: '0.8rem' }}>
                    {day}{i18next.t('common.day')}
                  </MenuItem>
              ))}
              <MenuItem key={0} value={0} sx={{ fontSize: '0.8rem' }}>
                {i18next.t('user.end_of_month')}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDueDateDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handleDueDateChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
          open={paymentMonthDialogOpen}
          onClose={() => setPaymentMonthDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('user.change_payment_month')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
                autoFocus
                margin="dense"
                value={selectedPaymentMonth}
                onChange={(e) => setSelectedPaymentMonth(Number(e.target.value))}
                sx={{ mt: 1 }}
            >
              <MenuItem value={1} sx={{ fontSize: '0.8rem' }}>
                {i18next.t('user.next_month')}
              </MenuItem>
              <MenuItem value={2} sx={{ fontSize: '0.8rem' }}>
                {i18next.t('user.month_after_next')}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentMonthDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handlePaymentMonthChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
          open={paymentDateDialogOpen}
          onClose={() => setPaymentDateDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('user.change_payment_date')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
                autoFocus
                margin="dense"
                value={selectedPaymentDate}
                onChange={(e) => setSelectedPaymentDate(Number(e.target.value))}
                sx={{ mt: 1 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflowY: 'auto'
                    }
                  }
                }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day} sx={{ fontSize: '0.8rem' }}>
                    {day}{i18next.t('common.day')}
                  </MenuItem>
              ))}
              <MenuItem key={0} value={0} sx={{ fontSize: '0.8rem' }}>
                {i18next.t('user.end_of_month')}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDateDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handlePaymentDateChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={preferredLanguageDialogOpen}
        onClose={() => setPreferredLanguageDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('user.preferred_language')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
              autoFocus
              margin="dense"
              value={selectedPreferredLanguage}
              onChange={(e) => setSelectedPreferredLanguage(e.target.value)}
              sx={{ mt: 1 }}
            >
              {supportedLanguages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>{lang.nativeName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreferredLanguageDialogOpen(false)}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handlePreferredLanguageChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNewContactDialog}
        onClose={handleCloseNewContactDialog}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t('contact.add_contact')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Select
              value={newContactType}
              onChange={(e) => setNewContactType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="EMAIL">{i18next.t('contact.email_label')}</MenuItem>
              <MenuItem value="PHONE">{i18next.t('contact.phone')}</MenuItem>
              <MenuItem value="FAX">{i18next.t('contact.fax')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label={i18next.t('user.contact')}
            type="text"
            fullWidth
            value={newContactValue}
            onChange={(e) => setNewContactValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewContactDialog}>
            {i18next.t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddNewContact}
            variant="contained"
            color="primary"
            disabled={!newContactValue.trim()}
          >
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNewAddressDialog}
        onClose={() => {
          setOpenNewAddressDialog(false);
          setNewAddress({
            id: 0,
            userId: '',
            postalCode: '',
            prefecture: '',
            city: '',
            streetAddress: '',
            building: '',
            aliasName: '',
            isDefault: 0,
            createdAt: '',
            updatedAt: '',
          });
          setEditingAddress(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{i18next.t(editingAddress ? 'address.address_edit' : 'user.add_address')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={newAddress.isDefault === 1 ? 'default' : 'additional'}
                  onChange={(e) => setNewAddress({
                    ...newAddress,
                    isDefault: e.target.value === 'default' ? 1 : 0
                  })}
                >
                  <FormControlLabel value="default" control={<Radio />} label={i18next.t('user.default_address')} />
                  {(!editingAddress || user?.addresses.length > 1) &&
                      <FormControlLabel value="additional" control={<Radio />} label={i18next.t('address.additional_address')} />
                  }
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label={i18next.t('address.postal_code')}
                value={newAddress.postalCode}
                onChange={(e) => handlePostalCodeChange(e.target.value)}
                placeholder={i18next.t('address.postal_code_example')}
                helperText={postalCodeError || i18next.t('address.hyphen_optional')}
                error={!!postalCodeError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handlePostalCodeSearch}
                        startIcon={<SearchIcon />}
                        size="small"
                        disabled={!isValidPostalCode(newAddress.postalCode)}
                      >
                        {i18next.t('address.search_address')}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label={i18next.t('address.prefecture')}
                value={newAddress.prefecture}
                onChange={(e) => handleAddressChange('prefecture', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label={i18next.t('address.city')}
                value={newAddress.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label={i18next.t('address.detail_address')}
                value={newAddress.streetAddress}
                onChange={(e) => handleAddressChange('streetAddress', e.target.value)}
                helperText={i18next.t('address.building_hint')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={i18next.t('address.building_name')}
                value={newAddress.building}
                onChange={(e) => handleAddressChange('building', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={i18next.t('address.alias')}
                value={newAddress.aliasName}
                onChange={(e) => handleAddressChange('aliasName', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewAddress}>
            {i18next.t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddNewAddress}
            variant="contained"
            color="primary"
            disabled={!newAddress.postalCode || !newAddress.prefecture || !newAddress.city || !newAddress.streetAddress}
          >
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetail;
